/**
 * Route guards, and the order they run in.
 *
 * A direct port of the gate mechanism in the Flutter base template, including
 * its numbers — `core_access`'s `AccessGate` runs at priority 35, after the
 * session gates at 30 — because the two clients must reach the same conclusion
 * about the same route. A web app that redirected to sign-in where the mobile
 * app showed "forbidden" would be the same product behaving differently for no
 * reason the user could see.
 *
 * The ordering is not a preference. **Authentication must run before
 * authorisation**, and the reason is what the user is told: an anonymous
 * visitor hitting an admin URL should be asked to sign in, not told they lack
 * permission. Told the latter, they cannot act on it — they have no account to
 * grant a permission to. Running the access gate first would produce exactly
 * that message for every signed-out visitor.
 *
 * None of this is a security control. Every route it protects is backed by an
 * endpoint that re-authorises the request; the gates decide what to *navigate*
 * to.
 */

import type { AccessSnapshot } from "@hardauto/core-access";

/** Priorities, matching the Flutter template's numbering. */
export const GATE_PRIORITY = {
  /** Is the session still being restored? Blocks everything until known. */
  BOOTSTRAP: 10,
  /** Is anyone signed in? */
  SESSION: 30,
  /** May this principal see this route? */
  ACCESS: 35,
} as const;

/** What a gate is told about the attempted navigation. */
export interface GateContext {
  /** The path being navigated to, for building a return-to parameter. */
  location: string;
  /** False while the session is still being restored from the cookie. */
  sessionReady: boolean;
  /** Whether anyone is signed in. */
  authenticated: boolean;
  /** False while permissions are loading. */
  accessReady: boolean;
  snapshot: AccessSnapshot;
  /** What the route requires, if anything. */
  requirement: RouteRequirement | undefined;
}

/** What a route demands of the principal. */
export interface RouteRequirement {
  /** Requires a signed-in user. */
  authenticated?: boolean;
  permissions?: readonly string[];
  mode?: "all" | "any";
  /** Requires NOT being signed in — the sign-in page itself. */
  anonymousOnly?: boolean;
}

/** A gate's verdict. `null` means "no opinion, carry on". */
export type GateVerdict = { redirectTo: string; reason: string } | null;

export interface Gate {
  name: string;
  priority: number;
  evaluate: (context: GateContext) => GateVerdict;
}

export const BOOTSTRAP_PATH = "/loading";
export const SIGN_IN_PATH = "/sign-in";
export const FORBIDDEN_PATH = "/forbidden";
export const HOME_PATH = "/";

/** Query parameter carrying where to return after signing in. */
export const RETURN_TO_PARAM = "returnTo";

/**
 * A safety net for a router mounted before the session is known.
 *
 * **This should never fire.** The application must not mount the router until
 * `sessionReady` is true — see `createAppRouter`'s documentation — because
 * this gate can only *redirect*, and redirecting destroys the URL the user
 * asked for. Someone opening `/admin/users` with a valid cookie would land on
 * `/loading` and stay there, having lost their deep link to a spinner.
 *
 * It is kept because the alternative failure is worse: a gate that ADMITS a
 * navigation while the session is unknown renders the signed-out view of a
 * private page for a frame, and the user may click something in it. Failing
 * closed at a dead end beats failing open.
 *
 * Reaching this branch means the mounting invariant was broken. The gate tests
 * exercise it directly; in a correctly composed application it is unreachable.
 */
export const bootstrapGate: Gate = {
  name: "bootstrap",
  priority: GATE_PRIORITY.BOOTSTRAP,
  evaluate: ({ sessionReady, location }) =>
    sessionReady || location === BOOTSTRAP_PATH
      ? null
      : { redirectTo: BOOTSTRAP_PATH, reason: "session not restored yet" },
};

/** Require a session, or send the visitor to sign in. */
export const sessionGate: Gate = {
  name: "session",
  priority: GATE_PRIORITY.SESSION,
  evaluate: ({ authenticated, requirement, location }) => {
    if (requirement?.anonymousOnly && authenticated) {
      // Already signed in and looking at the sign-in page. Sending them home
      // is what makes a bookmarked /sign-in behave sensibly.
      return { redirectTo: HOME_PATH, reason: "already signed in" };
    }
    if (!requirement?.authenticated || authenticated) return null;

    // Carry the attempted path so the user lands where they were going, not on
    // a generic home page. Encoded, because it is a URL inside a URL.
    const returnTo = `?${RETURN_TO_PARAM}=${encodeURIComponent(location)}`;
    return {
      redirectTo: `${SIGN_IN_PATH}${returnTo}`,
      reason: "authentication required",
    };
  },
};

/**
 * Require a permission.
 *
 * Runs at 35, AFTER the session gate at 30, so it only ever judges an
 * established principal. That is what makes "forbidden" an honest answer: the
 * person seeing it has an account, and an administrator can act on it.
 */
export const accessGate: Gate = {
  name: "access",
  priority: GATE_PRIORITY.ACCESS,
  evaluate: ({ requirement, snapshot, accessReady, authenticated }) => {
    const required = requirement?.permissions ?? [];
    if (required.length === 0) return null;
    // The session gate has already redirected an anonymous visitor; reaching
    // here unauthenticated means the route asked for a permission but not for
    // authentication, which is a mistake in the route definition.
    if (!authenticated) return null;

    if (!accessReady) {
      // Same safety net, same caveat as `bootstrapGate`: the application holds
      // the router unmounted while permissions load, so this should not fire.
      // If it does, holding is still better than denying — denying would show
      // "forbidden" for a frame to a user who is in fact permitted.
      return { redirectTo: BOOTSTRAP_PATH, reason: "permissions not loaded yet" };
    }

    const allowed =
      requirement?.mode === "any" ? snapshot.canAnyOf(required) : snapshot.canAllOf(required);

    return allowed
      ? null
      : { redirectTo: FORBIDDEN_PATH, reason: `missing ${required.join(", ")}` };
  },
};

export const DEFAULT_GATES: readonly Gate[] = [bootstrapGate, sessionGate, accessGate];

/**
 * Run the gates in priority order and return the first verdict.
 *
 * First, not the strictest: the ordering encodes which answer is most useful,
 * and a later gate's opinion is only meaningful once the earlier ones have
 * passed.
 */
export function evaluateGates(
  context: GateContext,
  gates: readonly Gate[] = DEFAULT_GATES,
): GateVerdict {
  for (const gate of [...gates].sort((a, b) => a.priority - b.priority)) {
    const verdict = gate.evaluate(context);
    if (verdict) return verdict;
  }
  return null;
}

/** Read a safe return-to path from a query string.
 *
 * Only same-origin, absolute paths are honoured. A `returnTo` of
 * `https://evil.example` would otherwise turn the sign-in page into an open
 * redirect — a phishing primitive that looks like a legitimate link to the
 * real site.
 */
export function safeReturnTo(search: string, fallback: string = HOME_PATH): string {
  const value = new URLSearchParams(search).get(RETURN_TO_PARAM);
  if (!value) return fallback;
  // Must start with a single slash: `//evil.example` is protocol-relative and
  // sends the browser off-origin just as effectively as a full URL.
  if (!value.startsWith("/") || value.startsWith("//")) return fallback;
  return value;
}
